import type { ComboboxItem } from '@/components'
import { Checkbox, Combobox, UserCombobox } from '@/components'
import { MonthYearPicker } from '@/components/month-year-picker'
import {
  ExportCard,
  ExportField,
} from '@/modules/exports/components/export-form'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isAfter } from 'date-fns'
import { useExportTaxZoneReportForm, type TaxZoneReportType } from '../../hooks'

export const ExportFleetTaxZoneCard: React.FC = () => {
  const translations = useTranslate({
    title: 'fleet.tax-zone-report.title',
    fromMonth: 'fleet.tax-zone-report.from-month',
    toMonth: 'fleet.tax-zone-report.to-month',
    reportType: 'fleet.tax-zone-report.report-type',
    taxZones: 'fleet.tax-zone-report.tax-zones',
    countryAndTaxZones: 'fleet.tax-zone-report.country-and-tax-zones',
    drivers: 'fleet.tax-zone-report.users',
    excludeNonFleetVehicles: 'fleet.tax-zone-report.exclude-non-fleet-vehicles',
    allDrivers: ['fleet.tax-zone-report.all-users', { n: 0 }],
  })

  const { loading, form, submit, updateForm, handleExport, handler } =
    useExportTaxZoneReportForm()

  const reportTypeItems: ComboboxItem<TaxZoneReportType>[] = [
    { value: 'TAX_ZONES', label: translations.taxZones },
    { value: 'COUNTRY_AND_TAX_ZONES', label: translations.countryAndTaxZones },
  ]

  return (
    <ExportCard
      title={translations.title}
      loading={loading}
      onSubmit={submit(handleExport)}
    >
      <ExportField label={translations.fromMonth}>
        <MonthYearPicker
          value={form.monthStart}
          onChange={monthStart =>
            updateForm({
              monthStart,
              monthEnd: isAfter(monthStart, form.monthEnd)
                ? monthStart
                : form.monthEnd,
            })
          }
        />
      </ExportField>

      <ExportField label={translations.toMonth}>
        <MonthYearPicker
          value={form.monthEnd}
          onChange={monthEnd =>
            updateForm({
              monthEnd,
              monthStart: isAfter(form.monthStart, monthEnd)
                ? monthEnd
                : form.monthStart,
            })
          }
        />
      </ExportField>

      <ExportField label={translations.reportType}>
        <Combobox
          mode="single"
          noSearch
          value={form.reportType}
          items={reportTypeItems}
          onChange={value => handler('reportType')(value)}
        />
      </ExportField>

      <ExportField label={translations.drivers} wide>
        <UserCombobox
          mode="multiple"
          value={form.userIds}
          placeholder={items => translations.allDrivers({ n: items.length })}
          onChange={value => updateForm({ userIds: value })}
        />
      </ExportField>

      <label className="flex w-fit cursor-pointer items-center gap-3 text-sm font-medium text-stone-700 md:col-span-2">
        <Checkbox
          size="sm"
          value={form.excludeNonFleetVehicles}
          onChange={checked =>
            updateForm({ excludeNonFleetVehicles: !!checked })
          }
        />
        {translations.excludeNonFleetVehicles}
      </label>
    </ExportCard>
  )
}
